import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { colors } from '../styles/GlobalStyles';
import { Button } from '../components/Button';
import { Input, Label, FormGroup, ErrorMessage } from '../components/Input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Content = styled.div`
  max-width: 1200px;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BrandSection = styled.div`
  text-align: center;
`;

const Logo = styled.h1`
  font-size: 48px;
  color: ${colors.primary};
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Tagline = styled.p`
  font-size: 20px;
  color: ${colors.textLight};
  margin-bottom: 40px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const LoginSection = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 20px ${colors.shadow};

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 2px solid ${colors.secondary};
`;

const Tab = styled.button.attrs(props => ({
  active: undefined, // Remove from DOM
}))`
  flex: 1;
  padding: 12px;
  background: none;
  color: ${props => props.active ? colors.primary : colors.textLight};
  font-size: 16px;
  font-weight: 500;
  border-bottom: 3px solid ${props => props.active ? colors.primary : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.3s ease;

  &:hover {
    color: ${colors.primary};
  }
`;

const Title = styled.h2`
  color: ${colors.primary};
  margin-bottom: 30px;
  text-align: center;
`;

const ForgotPassword = styled.button`
  background: none;
  color: ${colors.primary};
  font-size: 14px;
  text-decoration: underline;
  margin-top: 10px;
  display: block;
  margin-left: auto;

  &:hover {
    opacity: 0.8;
  }
`;

export const Home = () => {
  const [activeTab, setActiveTab] = useState('member');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password, activeTab === 'admin');
      toast.success('Giriş başarılı!');
      
      if (activeTab === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/member/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Giriş yapılamadı');
      toast.error(err.response?.data?.error || 'Giriş yapılamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <Container>
      <Content>
        <BrandSection>
          <Logo>Seed Yoga Studio</Logo>
          <Tagline>Zihin, Beden ve Ruh Uyumu</Tagline>
          <p style={{ color: colors.textLight, marginBottom: '20px' }}>
            Rezervasyon sisteminize hoş geldiniz. Lütfen devam etmek için giriş yapın.
          </p>
          <Button 
            type="button"
            variant="secondary" 
            onClick={() => navigate('/public-schedule')}
            style={{ maxWidth: '300px', margin: '0 auto' }}
          >
            📅 Ders Programını Görüntüle
          </Button>
        </BrandSection>

        <LoginSection>
          <Tabs>
            <Tab 
              active={activeTab === 'member'} 
              onClick={() => setActiveTab('member')}
            >
              Üye Girişi
            </Tab>
            <Tab 
              active={activeTab === 'admin'} 
              onClick={() => setActiveTab('admin')}
            >
              Yönetici Girişi
            </Tab>
          </Tabs>

          <Title>
            {activeTab === 'member' ? 'Üye Girişi' : 'Yönetici Girişi'}
          </Title>

          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="ornek@email.com"
                autoComplete="off"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">Şifre</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <Button type="submit" fullWidth disabled={loading}>
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </Button>

            {activeTab === 'member' && (
              <ForgotPassword onClick={handleForgotPassword}>
                Şifremi Unuttum
              </ForgotPassword>
            )}
          </form>
        </LoginSection>
      </Content>
    </Container>
  );
};

